<script setup lang="ts">
import type { UIFooterProps } from '../../types'

const props = withDefaults(
    defineProps<UIFooterProps>(),
    {
        navigation: () => [],
        authority: () => ({ items: [] }),
        payment: () => [],
        social: () => []
    }
)

const handleOnClick = (type: string) => {
    if (type === 'openCookieSettings') {
        window.Cookiebot.renew()

        return
    }
}
</script>

<template>
    <footer class="footer">
        <div class="footer__section">
            <div class="footer__brand">
                <slot name="brand" />
            </div>

            <div class="footer__payment">
                <slot name="payment">
                    <ul class="list-none">
                        <li v-for="(paymentItem, index) in props.payment" :key="`payment-${index}`">
                            <NuxtImg
                                preset="local"
                                provider="cloudflare"
                                decondig="async"
                                loading="lazy"
                                v-bind="paymentItem"
                            />
                        </li>
                    </ul>
                </slot>
            </div>
        </div>

        <div class="footer__section">
            <nav class="footer__navigation">
                <slot name="navigation">
                    <div v-for="(navColumn, index) in props.navigation" :key="`column-${index}`">
                        <h4>{{ navColumn.title }}</h4>
                        <ul class="list-none">
                            <li v-for="(navItem, nestedIndex) in navColumn.items" :key="`item-${nestedIndex}`">
                                <NuxtLink
                                    :href="navItem?.onClick ? undefined : navItem.href"
                                    :target="navItem.target"
                                    :rel="navItem.rel"
                                    :data-test="navItem.dataCy"
                                    :external="navItem.external"
                                    @click="navItem?.onClick ? handleOnClick(navItem.onClick) : null"
                                >
                                    <component :is="navItem.title" v-if="typeof navItem.title === 'function'" />
                                    <span v-else v-text="navItem.title" />
                                </NuxtLink>
                            </li>
                        </ul>
                    </div>
                </slot>
            </nav>

            <div class="footer__authority">
                <slot name="authority">
                    <ul v-if="props.authority" class="list-none">
                        <li v-for="(authorityItem, index) in props.authority.items" :key="`authority-${index}`">
                            <NuxtLink
                                :href="authorityItem.href"
                                :rel="authorityItem.rel"
                                :target="authorityItem.target"
                                :external="authorityItem.external"
                            >
                                <NuxtImg
                                    preset="local"
                                    provider="cloudflare"
                                    decondig="async"
                                    loading="lazy"
                                    v-bind="authorityItem.image"
                                />
                            </NuxtLink>
                        </li>
                    </ul>

                    <!-- eslint-disable-next-line vue/no-v-html props.authority is safe -->
                    <p v-if="props.authority.permitText" v-html="props.authority.permitText" />
                    <!-- eslint-disable-next-line vue/no-v-html props.authority is safe -->
                    <p v-if="props.authority.responsibleText" data-test="responsible-gambling-text" v-html="props.authority.responsibleText" />
                </slot>

                <slot name="authorityExtras" />
            </div>
        </div>

        <div v-if="props.social.length || $slots.social" class="footer__social">
            <slot name="social">
                <ul class="list-none">
                    <li v-for="(socialLink, index) in props.social" :key="`social-${index}`">
                        <NuxtLink
                            :href="socialLink.url"
                            :aria-label="socialLink.ariaLabel"
                            rel="noopener"
                            target="_blank"
                            external
                        >
                            <UIIcon :name="socialLink.platform" />
                        </NuxtLink>
                    </li>
                </ul>
            </slot>
        </div>

        <div class="footer__section">
            <div class="footer__extras">
                <slot name="extras" />
            </div>
        </div>
    </footer>
</template>

<style lang="scss" scoped>
.footer {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: $gutter-size * 3;
    text-align: center;

    @media (min-width: $screen-md) {
        text-align: left;
    }

    &,
    h4,
    a,
    a:hover {
        color: var(--on-background, $footer-font-color);
    }

    h4 {
        margin-top: $gutter-size;

        @media (min-width: $screen-md) {
            margin-top: 0;
        }
    }

    &__section {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: $gutter-size * 3;

        @media (min-width: $screen-md) {
            flex-direction: row;
            justify-content: space-between;
        }
    }

    &__brand {
        justify-self: center;

        @media (min-width: $screen-md) {
            justify-self: flex-start;
        }
    }

    &__social {
        > ul {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            gap: 16px;

            a {
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }
        }
    }

    &__payment {
        justify-self: center;

        @media (min-width: $screen-md) {
            justify-self: flex-end;
        }

        > ul {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            gap: 35px 20px;

            @media (min-width: $screen-md) {
                justify-content: flex-end;
            }
        }
    }

    &__navigation {
        display: flex;
        flex-direction: column;
        gap: 15px;

        @media (min-width: $screen-md) {
            flex-direction: row;
            gap: 30px;
        }

        @media (min-width: $screen-xl) {
            width: auto;
            gap: 80px;
        }

        ul {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-bottom: 15px;

            li {
                cursor: pointer;
            }
        }

        h4 {
            font-family: $brand-font-family;
            font-weight: $text-bold;
            white-space: nowrap;
            margin-bottom: 15px;

            @media (min-width: $screen-md) {
                font-size: $font-size-lg;
            }
        }
    }

    &__authority {
        font-size: $font-size-base;

        @media (min-width: $screen-md) {
            justify-self: flex-end;
            font-size: $font-size-xs;
            text-align: right;
        }

        @media (min-width: $screen-lg) {
            min-width: 220px;
            width: auto;
        }

        > ul {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            gap: 35px 20px;

            @media (min-width: $screen-md) {
                justify-content: flex-end;
            }
        }

        p {
            @media (min-width: $screen-md) {
                font-size: $font-size-xs;
            }
        }
    }

    &__extras {
        width: 100%;
    }
}
</style>
